import { configService } from "@/infrastructure/services/ConfigService";
import { logger } from "@/infrastructure/services/LoggerService";
import { requiredCapability, resolveCapabilityRedirect, } from "@/shared/utils/routeCapabilityGuard";
/**
 * CONSTRAINT: `router.resolve` does NOT follow `redirect` (vue-router applies it
 * during navigation), so every check in this module must run on the destination the
 * navigation actually lands on — a redirect-only record ("/scheduling") carries no
 * meta, and validating it un-followed reopens the 403 dead end through the alias.
 * CONSTRAINT: a NAMED redirect gets the source params only during real navigation —
 * resolving the bare record throws ("/communication/:id" → CampaignInformation is
 * the in-repo shape) and the throw escapes into the guard's catch, a forced
 * re-login loop. Hence the params merge AND the catch. A function redirect is
 * unresolvable ahead of navigation → unusable, never guessed at.
 */
function followRedirects(router, configured) {
    const maxHops = 10;
    try {
        let target = router.resolve(configured);
        for (let hop = 0; hop < maxHops; hop++) {
            const record = target.matched[target.matched.length - 1];
            if (!record?.redirect) {
                return target;
            }
            const redirect = record.redirect;
            if (typeof redirect === "function") {
                return null;
            }
            target = router.resolve(typeof redirect === "string" || "path" in redirect
                ? redirect
                : { ...redirect, params: { ...target.params, ...redirect.params } });
        }
    }
    catch {
        return null;
    }
    return null;
}
/**
 * The final destination of a configured landing path (redirects followed), or
 * `null` + warning when unusable: no route; only the NotFound catch-all (it matches
 * every path — the route NAME is the discriminator, without which a ConfigMap typo
 * lands every user on a 404); or a loop-closing destination — the root ("/" is what
 * the landing redirect rewrites, forever) and login/public pages, which push back
 * to "/" themselves ("/session-ended" is the shell's instance).
 */
function resolveUsableLandingTarget(router, configured) {
    const target = followRedirects(router, configured);
    if (!target ||
        target.matched.length === 0 ||
        target.name === "NotFound" ||
        target.path === "/" ||
        target.meta?.isLoginPage ||
        target.meta?.isPublicPage) {
        logger.warn(`[landingPage] "${configured}" is not a usable landing route; falling back to "/".`);
        return null;
    }
    return target;
}
/**
 * The redirect target for a navigation, or `null` when it should proceed unchanged.
 *
 * CONSTRAINT: a landing page the user is not entitled to must degrade to "/", never
 * reach the capability guard — `/error/403` is a dead end (`ErrorPage`'s only exit
 * pushes "/", which this function rewrites back into the same 403). The pre-flight
 * runs the SAME `resolveCapabilityRedirect` the guard applies, so they cannot
 * disagree about entitlement.
 */
export async function resolveLandingRedirect(router, toPath, loadCapabilities) {
    if (toPath !== "/") {
        return null;
    }
    const landing = configService.landingPage;
    if (landing === "/") {
        return null;
    }
    const target = resolveUsableLandingTarget(router, landing);
    if (!target) {
        return null;
    }
    if (!requiredCapability(target)) {
        return landing;
    }
    let capabilities;
    try {
        capabilities = await loadCapabilities();
    }
    catch (error) {
        logger.warn(`[landingPage] could not load the user's capabilities; staying on "/" instead of redirecting to "${landing}".`, error);
        return null;
    }
    if (resolveCapabilityRedirect(target, capabilities)) {
        logger.warn(`[landingPage] "${landing}" requires a capability this user does not hold; staying on "/".`);
        return null;
    }
    return landing;
}
/** Exported for the spec — production reaches it only through `resolveLandingRedirect`. */
export { resolveUsableLandingTarget };
